/**
 * useInsightsData — thin adapter over InsightsContext's caches (Fix Spec C).
 * All fetching/caching lives in InsightsContext; this hook only (a) fires
 * the lazy `ensure*` calls when it mounts or its site/range args change, and
 * (b) reads the cached, memoized result back into the same `InsightsData`
 * shape the pages have always consumed. Lens math is pure client-side
 * (status-subset sums, `lensTotal` in `../types`) — untouched by this move.
 *
 * Because `ensureSiteData`/`ensureRangeData` no-op once a fetch is in flight
 * or done (see InsightsContext), remounting this hook — a tab switch
 * between /insights/studies and /insights/coordinators, or a StrictMode
 * double-invoke — never re-fetches: it just re-attaches to the same cache
 * entry.
 */

import { useEffect } from 'react';
import { useInsights } from '../contexts';
import type { EffortRow, VisitStatsRow, StudyInfo, Coordinator, DelegationProtocol, PortalAccessRow } from '../types';
import type { StudyRollup, CoordinatorRollup } from '../lib/rollups';

export { lensTotal } from '../types';
export { UNATTRIBUTED_KEY, coordinatorKey, studyKey, wholeVisitMinutes, workedStudyKeys } from '../lib/rollups';
export type { StudyRollup, CoordinatorRollup } from '../lib/rollups';

export interface InsightsData {
  effort: EffortRow[];
  visits: VisitStatsRow[];
  studies: StudyInfo[];
  coordinators: Coordinator[];
  delegations: DelegationProtocol[];
  portalAccess: PortalAccessRow[];
  /** Server-validated echo of the RPCs' tz classification. Not currently
   *  read by any component — kept in the API contract as-is. */
  tz: string | null;
  loading: boolean;
  effortLoading: boolean;
  error: string | null;
  /** The effort/visits (range) fetch specifically failed. Split out of the
   *  combined `error` because a failed range means the activity numbers are
   *  UNKNOWN, not zero: anything that reads absent minutes as a finding —
   *  the data-quality banner, the "enrolling but idle" flags, the "could
   *  spend time here" gaps — must stay silent rather than render confident
   *  zeros behind an error box. */
  effortError: string | null;
  studyRollups: Map<string, StudyRollup>;
  coordinatorRollups: Map<string, CoordinatorRollup>;
  /** Evicts the cached data for this site+range and re-fetches all 5
   *  endpoints (studies, coordinators, delegations, portal-access, effort).
   *  Data-only — never triggers a full page reload. No-op with no site. */
  refresh: () => void;
}

export function useInsightsData(siteId: string | null, from: string, to: string): InsightsData {
  const ctx = useInsights();

  // Lazy fetch triggers — the ONLY place a fetch can originate from. This
  // effect (and the one below) only runs because a mounted Insights page
  // called this hook; InsightsProvider itself never calls ensure*.
  useEffect(() => {
    if (!siteId) return;
    ctx.ensureSiteData(siteId);
    // ensureSiteData is stable (see InsightsContext) and intentionally not a
    // dep: re-running this effect should only happen when siteId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    if (!siteId || !from || !to) return;
    ctx.ensureRangeData(siteId, from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, from, to]);

  const siteEntry = siteId ? ctx.getSiteEntry(siteId) : undefined;
  const rangeEntry = siteId && from && to ? ctx.getRangeEntry(siteId, from, to) : undefined;
  const rollups =
    siteId && from && to
      ? ctx.getRollups(siteId, from, to)
      : { studyRollups: new Map<string, StudyRollup>(), coordinatorRollups: new Map<string, CoordinatorRollup>() };

  // Mirrors the original hook's semantics: "loading" until the first
  // settle (success OR error clears it), "error" surfaces the message
  // without staying "loading".
  const loading = siteEntry ? siteEntry.status === 'loading' : Boolean(siteId);
  const effortLoading = rangeEntry ? rangeEntry.status === 'loading' : Boolean(siteId && from && to);
  const effortError = rangeEntry?.status === 'error' ? rangeEntry.error : null;
  const error = effortError || (siteEntry?.status === 'error' ? siteEntry.error : null);

  return {
    effort: rangeEntry?.data?.effort ?? [],
    visits: rangeEntry?.data?.visits ?? [],
    studies: siteEntry?.data?.studies ?? [],
    coordinators: siteEntry?.data?.coordinators ?? [],
    delegations: siteEntry?.data?.delegations ?? [],
    portalAccess: siteEntry?.data?.portalAccess ?? [],
    tz: rangeEntry?.data?.tz ?? null,
    loading,
    effortLoading,
    error,
    effortError,
    studyRollups: rollups.studyRollups,
    coordinatorRollups: rollups.coordinatorRollups,
    refresh: () => {
      if (siteId && from && to) ctx.refresh(siteId, from, to);
    },
  };
}

export default useInsightsData;
